import { iMapSummaryBlockData } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { GoogleMapImage } from "../../google-maps-helpers/google-map-static";
import { useEffect, useState } from "react";
import { quoteThemeColorStyles } from "../quote-theme/quote-theme-css-vars";

interface MapSummarySectionProps {
  data: iMapSummaryBlockData;
  primaryColor?: string;
}

// Custom hook to get screen size
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

// Function to calculate map size based on screen size
const calculateMapSize = (screenWidth: number) => {
  // For mobile devices, use full width minus padding
  if (screenWidth < 768) {
    const mobileWidth = screenWidth - 32; // 16px padding on each side
    const mobileHeight = Math.round((mobileWidth * 9) / 16);
    return `${mobileWidth}x${mobileHeight}`;
  }
  
  // For larger screens, use a more reasonable size
  const containerWidth = Math.min(screenWidth - 64, 800); // Reduced max width
  const height = Math.round((containerWidth * 9) / 16);
  
  // Ensure minimum and maximum sizes
  const minWidth = 300;
  const maxWidth = 800; // Reduced from 1200
  const minHeight = 200;
  const maxHeight = 450; // Reduced from 600
  
  const finalWidth = Math.max(minWidth, Math.min(maxWidth, containerWidth));
  const finalHeight = Math.max(minHeight, Math.min(maxHeight, height));
  
  return `${finalWidth}x${finalHeight}`;
};

export function MapSummarySection({
  data,
  primaryColor,
}: MapSummarySectionProps) {
  const screenSize = useScreenSize();
  const isMobile = screenSize.width < 768;
  
  // Calculate center point for the map
  const calculateMapCenter = () => {
    if (data.places.length === 0) return { lat: 0, lng: 0 };

    const totalLat = data.places.reduce(
      (sum, place) => sum + (place.geometry?.location?.lat || 0),
      0
    );
    const totalLng = data.places.reduce(
      (sum, place) => sum + (place.geometry?.location?.lng || 0),
      0
    );

    return {
      lat: totalLat / data.places.length,
      lng: totalLng / data.places.length,
    };
  };

  const center = calculateMapCenter();
  const zoom = data.zoomLevel || 10;

  // Create Google Maps URL for interactive iframe
  const createInteractiveMapUrl = () => {
    const apiKey = "AIzaSyBB3tgId37CgrL1JoGU2do45xm_gXH_UM4";
    
    if (data.places.length === 0) {
      return null;
    }
    
    if (data.places.length === 1) {
      // Single place - use place mode
      const place = data.places[0];
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=place_id:${place.place_id}&zoom=${zoom}`;
    } else {
      // Multiple places - use search mode with coordinates
      // Create a search query with the first few place names
      const searchQuery = data.places.slice(0, 3).map(place => place.name).join(', ');
      
      return `https://www.google.com/maps/embed/v1/search?key=${apiKey}&q=${encodeURIComponent(searchQuery)}&center=${center.lat},${center.lng}&zoom=${zoom}`;
    }
  };

  // Create static map parameters
  const createStaticMapParams = () => {
    if (data.places.length === 0) return null;

    const centerStr = `${center.lat},${center.lng}`;
    
    // Create markers for multiple places
    const markers = data.places.map((place, index) => {
      const lat = place.geometry?.location?.lat;
      const lng = place.geometry?.location?.lng;
      if (lat && lng) {
        const color = primaryColor?.replace("#", "") || "red";
        return `${lat},${lng}`;
      }
      return null;
    }).filter(Boolean).join('|');

    // Dynamically calculate size based on screen size
    const dynamicSize = calculateMapSize(screenSize.width);

    return {
      center: centerStr,
      markers: markers,
      zoom: zoom,
      size: dynamicSize
    };
  };

  const interactiveMapUrl = createInteractiveMapUrl();
  const staticMapParams = createStaticMapParams();

  // Places list component
  const PlacesList = () => (
    <div className={`bg-white/95 backdrop-blur-sm rounded-lg p-3 ${isMobile ? 'mb-4' : 'max-w-xs'}`}>
      <div className="text-sm font-medium mb-2">Places on Map</div>
      <div className="space-y-1 max-h-32 overflow-y-auto">
        {data.places.map((place, index) => (
          <div
            key={place.place_id}
            className="flex items-center space-x-2 text-xs"
          >
            <div
              className="w-3 h-3 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={quoteThemeColorStyles.primaryBg}
            >
              {index + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{place.name}</div>
              <div className="text-muted-foreground truncate text-xs">
                {place.formatted_address}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
      {(data.mapTitle || data.mapDescription) && (
        <div className="flex flex-col space-y-1.5 p-6">
          {data.mapTitle && (
            <h3
              className="text-xl font-semibold leading-none tracking-tight"
              style={quoteThemeColorStyles.primary}
            >
              {data.mapTitle}
            </h3>
          )}
          {data.mapDescription && (
            <p className="text-muted-foreground mt-2">{data.mapDescription}</p>
          )}
        </div>
      )}
      <div className="p-6 pt-0">
        {data.places.length === 0 && (
          <div className="relative">
            <p className="text-muted-foreground">No places added to the map</p>
          </div>
        )}
        {data.places.length > 0 && (
          <div className="relative">
            {/* Mobile: Places list on top */}
            {isMobile && <PlacesList />}
            
            {/* Map container */}
            <div className="flex justify-center">
              {data.showMapControls ? (
                // Interactive map with controls
                interactiveMapUrl && (
                  <div className="w-full rounded-lg overflow-hidden">
                    <iframe
                      src={interactiveMapUrl}
                      width="100%"
                      height="400"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="rounded-lg"
                    />
                  </div>
                )
              ) : (
                // Static map image
                staticMapParams && (
                  <div className="w-full rounded-lg overflow-hidden flex justify-center">
                    <GoogleMapImage
                      center={staticMapParams.center}
                      size={staticMapParams.size}
                      zoom={staticMapParams.zoom}
                      marker={staticMapParams.markers}
                      mapType="roadmap"
                    />
                  </div>
                )
              )}
            </div>

            {/* Desktop: Places list overlay */}
            {!isMobile && (
              <div className="absolute top-4 right-4">
                <PlacesList />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
